import { LitElement, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import "@phosphor-icons/webcomponents/PhCheckCircle";
import "@phosphor-icons/webcomponents/PhPlus";

import { DataQueryController } from "ui-common";
import { getCreators } from "../../queries/get-creators";
import { getAssetUrl } from "helpers/assetHelpers";

import { styles } from "./creator-follow.css";
import "ui-components/form-elements/input/wow-input.ts";
import "ui-components/button/wow-button.ts";
import { t } from "../../translations/i18n";

interface Creator {
  id: string;
  handle: string | null;
  profileImage: string | null;
  profileImagePath: string | null;
  bio: string | null;
  isFollowed: boolean;
}

@customElement("creator-follow")
export class CreatorFollow extends LitElement {
  static override styles = [styles];

  @property({ type: Array })
  followedCreators: string[] = [];

  @property({ type: Array })
  categoryIds: string[] = [];

  @state()
  private selectedCreators = new Set<string>();

  @state()
  private searchTerm = "";

  @state()
  private imageErrors = new Set<string>();

  private readonly LIMIT = 20;
  private searchDebounceTimeout?: ReturnType<typeof setTimeout>;

  private creatorsQuery = new DataQueryController(this, () =>
    getCreators(
      this.LIMIT,
      0,
      false, //personalize
      this.searchTerm || undefined,
      // DISABLED TEMPORARILY
      // this.categoryIds.length > 0 ? this.categoryIds : undefined,
    ),
  );

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }
  }

  override willUpdate(changedProperties: Map<string, unknown>) {
    super.willUpdate(changedProperties);

    // Trigger refetch when search or category filters change
    if (changedProperties.has("searchTerm") || changedProperties.has("categoryIds")) {
      // Don't process old results during filter change
      return;
    }
  }

  override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);

    // Check if search or categories changed
    if (changedProperties.has("searchTerm") || changedProperties.has("categoryIds")) {
      // After observer has switched to new query, invalidate and refetch it
      this.creatorsQuery.invalidate();
    }
  }

  private handleSearch(e: Event) {
    const input = e.target as HTMLInputElement;
    const newValue = input.value;

    // Debounce the search to avoid too many requests
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }

    this.searchDebounceTimeout = setTimeout(() => {
      this.searchTerm = newValue;
    }, 300);
  }

  private handleCreatorToggle(creatorId: string) {
    if (this.selectedCreators.has(creatorId)) {
      this.selectedCreators.delete(creatorId);
    } else {
      this.selectedCreators.add(creatorId);
    }

    this.requestUpdate();
    this.dispatchFollowEvent();
  }

  private dispatchFollowEvent() {
    const selectedArray = Array.from(this.selectedCreators);

    this.dispatchEvent(
      new CustomEvent("creators-selected", {
        detail: { creators: selectedArray },
        bubbles: true,
      }),
    );
  }

  private handleImageError(creatorId: string) {
    this.imageErrors = new Set(this.imageErrors).add(creatorId);
  }

  private hasImageError(creatorId: string): boolean {
    return this.imageErrors.has(creatorId);
  }

  private renderCreatorCard(creator: Creator) {
    const isSelected = this.selectedCreators.has(creator.id);
    const profileImageUrl = creator.profileImagePath || creator.profileImage;
    const imageUrl = profileImageUrl ? getAssetUrl(profileImageUrl) : undefined;

    return html`
      <div
        class=${classMap({
          "creator-card": true,
          "creator-card-selected": isSelected,
        })}
        @click=${() => this.handleCreatorToggle(creator.id)}
      >
        <div class="creator-card-image">
          ${imageUrl
            ? html`<img
                  src=${imageUrl}
                  alt=${creator.handle || "Creator"}
                  @error=${() => this.handleImageError(creator.id)}
                  style="display: ${this.hasImageError(creator.id) ? "none" : "block"}"
                />
                ${this.hasImageError(creator.id)
                  ? html`<div class="creator-card-placeholder">${creator.handle?.charAt(0)?.toUpperCase() || "?"}</div>`
                  : nothing}`
            : html`<div class="creator-card-placeholder">${creator.handle?.charAt(0)?.toUpperCase() || "?"}</div>`}
        </div>
        <div class="creator-card-info">
          <div class="creator-card-handle">@${creator.handle || "unknown"}</div>
        </div>
        <div class="creator-card-follow">
          <div class="follow-action">
            ${isSelected
              ? html`<ph-check-circle weight="bold"></ph-check-circle>`
              : html`
                  <ph-plus></ph-plus>
                  <span>Add</span>
                `}
          </div>
        </div>
      </div>
    `;
  }

  protected override render() {
    const queryResult = this.creatorsQuery.result;
    const creators = queryResult.data?.influencers ?? [];
    const isLoading = queryResult.isLoading;

    return html`
      <div class="creator-follow">
        <div class="creator-follow-search">
          <wow-input placeholder=${t("sign-up.searchCreators")} @input=${this.handleSearch} .value=${this.searchTerm}>
          </wow-input>
        </div>

        ${isLoading
          ? html`<div class="creator-follow-loading">Loading creators...</div>`
          : creators.length > 0
            ? html`
                <div class="creator-follow-list">${creators.map((creator) => this.renderCreatorCard(creator))}</div>
              `
            : html`
                <div class="creator-follow-empty">
                  <div class="creator-follow-empty-title">
                    ${this.searchTerm ? "No creators found" : "No creators available"}
                  </div>
                  <div class="creator-follow-empty-subtitle">
                    ${this.searchTerm
                      ? "Try adjusting your search terms or explore different keywords"
                      : "Check back later for new creators to follow"}
                  </div>
                </div>
              `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "creator-follow": CreatorFollow;
  }
}
